import { Overlay, OverlayRef } from '@angular/cdk/overlay';
import { TemplatePortal } from '@angular/cdk/portal';
import {
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  EventEmitter,
  inject,
  Input,
  OnInit,
  Output,
  TemplateRef,
  ViewChild,
  ViewContainerRef
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormGroup, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { fromEvent } from 'rxjs';
import { CinemaWeek, DatePickerDate, Range } from '../../../core/types';
import { ChoiceModes } from './core/enums';
import { ScrollStrategyOptions } from '@angular/cdk/overlay';
import { BaseComponent } from '../../../core/layout/components/abstracts/base.component';
import { ControlValueAccessorUtils } from '../../../core/utils/ControlValueAccessorUtils';
import { ControlValueAccessorMixin } from '../../../core/mixins/ControlValueAccessorMixin';
import { Timeout } from '../../../core/decorators';
import { ICinemaWeek } from '../../../core/interfaces/ICinemaWeek';
import { CalendarUtils } from '../../../core/utils/CalendarUtils';
import { IDateToRender } from '../../../core/interfaces/IDateToRender';
import dayjs from 'dayjs';
import { cloneDeep } from 'lodash';
import { KwPipe } from '../../pipes/kw.pipe';
import { NgTemplateOutlet, UpperCasePipe } from '@angular/common';
import { FormControlComponent } from '../form-control/form-control.component';
import { MaxWidthDirective } from '../../directives/max-width.directive';
import { TranslatePipe } from '../../pipes/trans.pipe';
import { DayjsFormatPipe } from '../../pipes/dayjs-format.pipe';
import { MonthNumberToNamePipe } from '../../pipes/month-number-to-name.pipe';
import { DayjsStartOfPipe } from '../../pipes/dayjs-start-of.pipe';
import { CloneDeepPipe } from '../../pipes/clone-deep.pipe';
import { ShowsByDatePipe } from '../../pipes/shows-by-date.pipe';
import { ShowsStore } from '../../../features/shows/core/stores/ShowsStore';
import { ShowsService } from '../../../features/shows/core/services/ShowsService';
import { ApiErrorResponse } from '../../../core/classes/ApiErrorResponse';

@Component({
  selector: 'app-datepicker',
  templateUrl: './datepicker.component.html',
  styleUrls: ['./datepicker.component.scss'],
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    KwPipe,
    ReactiveFormsModule,
    NgTemplateOutlet,
    FormControlComponent,
    MaxWidthDirective,
    TranslatePipe,
    FormsModule,
    DayjsFormatPipe,
    UpperCasePipe,
    MonthNumberToNamePipe,
    DayjsStartOfPipe,
    CloneDeepPipe,
    ShowsByDatePipe
  ],
  providers: [
    ControlValueAccessorUtils.getProvider(DatepickerComponent),
  ]
})
export class DatepickerComponent extends ControlValueAccessorMixin<DatePickerDate | Range | number | CinemaWeek, BaseComponent>(BaseComponent) implements OnInit {

  @ViewChild('datePickerWrapper') datePickerWrapper: ElementRef<HTMLDivElement>;
  @ViewChild('pickerLayer') pickerLayer: TemplateRef<HTMLElement>;

  @Input() formGroup: FormGroup;
  @Input() isRequired = false;
  @Input() placeholder = '';
  @Input() label: string;
  @Input() hasErrors = false;
  @Input() pickerType: 'day' | 'range' | 'month' | 'year' | 'kw' = 'day';
  @Input() enlargePickerLayer = true;
  @Input() showTime = false;
  @Input() theme: 'white' | 'blue' = 'white';
  @Input() withTime = false;
  @Input() isForShows = false;
  @Input() plainView = false;
  @Input() isDayMonthView = false;
  @Input() set isPickerOpened(val: boolean) {
    if (this._isPickerOpened !== val) {
      this._isPickerOpened = val;
      this.cdr.detectChanges();
      this._isPickerOpened ? this.setOverlay() : this.close(false);

      if (this._isPickerOpened) {
        this.loadShows();
      }
    }
  };
  @Input() cxMinWidth = '100%';
  @Input() cxMaxWidth = '100%';
  @Input() outputType: 'dayjs' | 'date' | 'string' = 'string';
  @Input() override isDisabled = false;

  @Input() set min(min: DatePickerDate) {
    if (min) {
      this.minDate = this.pickerType === 'day' ? dayjs(min) : dayjs(min).startOf('day');
    }
  };

  @Input() set max(max: DatePickerDate) {
    if (max) {
      this.maxDate = this.pickerType === 'day' ? dayjs(max) : dayjs(max).startOf('day');
      this.validateMinMaxDate();
    }
  };

  @Output() valueChanged = new EventEmitter<DatePickerDate | Range | number | CinemaWeek>();
  @Output() dateChanged = new EventEmitter<DatePickerDate>();

  public isRangePicker = false;
  public isKWPicker = false;
  public inputValue;
  public minDate: dayjs.Dayjs;
  public maxDate: dayjs.Dayjs;
  public selectedVal = dayjs();
  public selectedTime = dayjs(this.selectedVal);
  public datesToRender: IDateToRender[] = [];
  public currentDateToShow = dayjs(this.selectedVal);
  public years: number[] = [];
  public range = {
    from: dayjs().startOf('day'),
    to: dayjs().add(1, 'day').startOf('day')
  };
  public selectedCinemaWeek: ICinemaWeek;
  public weekDays = cloneDeep(dayjs.weekdaysMin());
  public cinemaWeeks: ICinemaWeek[] = [];
  public currentHoveredCinemaWeek: number = null;
  public currentFocusedCinemaWeek: number = null;
  public inputSelectionStart: number;
  public readonly months = dayjs.months();
  public readonly choiceModes = ChoiceModes;
  public readonly showsStore = inject(ShowsStore);

  private _isPickerOpened = false;
  private _overlayRef: OverlayRef;
  private _currentChoiceMode: ChoiceModes = ChoiceModes.DAYS;
  private _intersectionObserver: IntersectionObserver;
  private readonly _overlay = inject(Overlay);
  private readonly _viewContainerRef = inject(ViewContainerRef);
  private readonly _scrollStrategyOptions = inject(ScrollStrategyOptions);
  private readonly _showsService = inject(ShowsService);

  public ngOnInit(): void {
    this.namespace = 'datepicker';

    this.setPickerType();
    this.selectedCinemaWeek = CalendarUtils.getCinemaWeek(this.currentDateToShow);
    this.isRangePicker = this.pickerType === 'range';
    this.isKWPicker = this.pickerType === 'kw';

    this.weekDays.push(this.weekDays.shift());

    if (this.isRangePicker) {
      this.selectedVal = dayjs(this.range.from);
      this.currentDateToShow = dayjs(this.selectedVal);
    } else if (this.isKWPicker) {
      this.weekDays.push(...this.weekDays.splice(0, 3));
    }

    this.setInputValue();
    this.generateCalendar();

    fromEvent(window, 'click').pipe(
      takeUntilDestroyed(this.destroyRef),
    ).subscribe(this.onClickAnywhere.bind(this))
  }

  public ngAfterViewInit(): void {
    this.setupIntersectionObserver();
  }

  public get currentChoiceMode(): ChoiceModes {
    return this._currentChoiceMode;
  }

  public get isPickerOpened(): boolean {
    return this._isPickerOpened;
  }

  @Timeout()
  public dateChange(date: dayjs.Dayjs): void {
    this.currentDateToShow = date;

    this.generateCalendar();

    if (!this.showTime) {
      this.close();
    } else {
      this.selectedVal = this.currentDateToShow;
      this.onTimeChange();
    }

    this.setInputValue();
    this.cdr.detectChanges();

    this.dateChanged.emit(this.selectedVal);
  }

  public cinemaWeekChange(date: dayjs.Dayjs): void {
    const cinemaWeek = CalendarUtils.getCinemaWeek(date);
    this.currentDateToShow = dayjs(cinemaWeek.from);
    this.selectedCinemaWeek = cinemaWeek;

    this.generateCalendar();
    this.close();
  }

  public onTimeChange(): void {
    if (this.selectedVal && this.selectedTime) {
      if (this.minDate && this.selectedVal.format('YYYY-MM-DD') === this.minDate.format('YYYY-MM-DD')) {
        if (this.minDate.hour() > this.selectedTime.hour()) {
          this.selectedTime = this.selectedTime.set('hour', this.minDate.hour());
        }

        if (
          this.minDate.hour() === this.selectedTime.hour() &&
          this.minDate.minute() > this.selectedTime.minute()
        ) {
          this.selectedTime = this.selectedTime.set('minute', this.minDate.minute());
        }
      }

      if (this.maxDate && this.selectedVal.format('YYYY-MM-DD') === this.maxDate.format('YYYY-MM-DD')) {
        if (this.selectedTime.hour() > this.maxDate.hour()) {
          this.selectedTime = this.selectedTime.set('hour', this.maxDate.hour());
        }

        if (
          this.maxDate.hour() === this.selectedTime.hour() &&
          this.selectedTime.minute() > this.maxDate.minute()
        ) {
          this.selectedTime = this.selectedTime.set('minute', this.maxDate.minute());
        }
      }
    }
  }

  @Timeout()
  public rangeChange(date: dayjs.Dayjs): void {
    this.currentDateToShow = date;

    if ((this.range.from && this.range.to) || (!this.range.from && !this.range.to)) {
      this.range.from = date;
      this.range.to = null;
    } else if (this.range.from && !this.range.to) {
      this.range.to = date;
    }

    this.range = cloneDeep(this.range);

    if (this.range.from && this.range.to) {
      if (this.range.to.format('x') < this.range.from.format('x')) {
        this.range = {
          from: this.range.to,
          to: this.range.from
        };
      }

      this.currentDateToShow = this.range.from;

      this.generateCalendar();
      this.close();
    }

    this.cdr.detectChanges();
  }

  public clearDate(): void {
    this.reset();
    this.close(true, null);
  }

  public override writeValue(val: DatePickerDate | Range | number | CinemaWeek): void {
    if (val) {
      if (this.isRangePicker) {
        this.range = {
          from: dayjs.isDayjs((val as Range).from)
            ? ((val as Range).from as dayjs.Dayjs)?.startOf('day')
            : dayjs((val as Range).from).startOf('day'),
          to: dayjs.isDayjs((val as Range).to)
            ? ((val as Range).to as dayjs.Dayjs)?.startOf('day')
            : dayjs((val as Range).to).startOf('day'),
        };

        if (this.range.from) {
          this.currentDateToShow = dayjs(this.range.from);
        } else if (this.minDate) {
          this.currentDateToShow = dayjs(this.minDate);
        } else {
          this.currentDateToShow = dayjs();
        }

      } else if (this.isKWPicker && ((val as CinemaWeek).hasOwnProperty('from') && (val as CinemaWeek).from)) {
        let cinemaWeek: ICinemaWeek;

        if ((val as CinemaWeek).hasOwnProperty('from')) {
          cinemaWeek = CalendarUtils.getCinemaWeek((val as CinemaWeek).from);
        } else {
          cinemaWeek = CalendarUtils.getCinemaWeek((val as DatePickerDate));
        }

        this.selectedCinemaWeek = {
          ...cinemaWeek
        };
        this.currentDateToShow = dayjs(this.selectedCinemaWeek.from);
      } else {
        this.selectedVal = dayjs(val as DatePickerDate);
        this.selectedTime = dayjs(this.selectedVal);
        this.currentDateToShow = dayjs(this.selectedVal);

        this.setInputValue();
      }
    } else {
      this.selectedVal = null;
      this.selectedTime = null;
      this.inputValue = '';

      this.reset();
    }

    this.validateMinMaxDate();
    this.generateCalendar();
  }

  @Timeout()
  public arrowClick(isLeft = false): void {
    const date = cloneDeep(this.currentDateToShow);
    const unit = [ChoiceModes.MONTHS, ChoiceModes.YEARS].includes(this.currentChoiceMode) ? 'year' : 'month';
    this.currentDateToShow = isLeft ? date.subtract(1, unit) : date.add(1, unit);
    this.loadShows();
  }

  public backToToday(): void {
    this.currentDateToShow = dayjs();

    switch (this.pickerType) {
      case 'range': this.rangeChange(this.currentDateToShow);
        break;

      case 'day': this.dateChange(this.currentDateToShow);
        break;

      case 'month': this.monthChange(this.currentDateToShow.month());
        break;

      case 'year': this.yearChange(this.currentDateToShow.year());
        break;

      case 'kw': this.cinemaWeekChange(this.currentDateToShow);
    }

    this.generateCalendar();
  }

  @Timeout()
  public monthChange(month: number): void {
    this.currentDateToShow = dayjs(this.currentDateToShow).set('month', month);

    this.setPickerType();

    if (this.currentChoiceMode === ChoiceModes.MONTHS) {
      this.close();
    }

    this.loadShows();
  }

  @Timeout()
  public yearChange(year: number): void {
    this.currentDateToShow = dayjs(this.currentDateToShow).set('year', year);
    this.inputValue = year;

    this.setPickerType();

    if (this.currentChoiceMode === ChoiceModes.YEARS) {
      this.close();
    }

    this.loadShows();
    this.setInputValue();
  }

  @Timeout()
  public changeCurrentChoiceMode(currentChoiceMode: ChoiceModes): void {
    if (this.isDayMonthView && currentChoiceMode === ChoiceModes.YEARS) {
      return;
    }
    this.currentChoiceMode = currentChoiceMode;
  }

  public close(setValue = true, val = this.currentDateToShow): void {
    if (!this.isRangePicker && !this.isKWPicker) {
      if (setValue) {
        this.selectedVal = val;
      }
    }

    this.validateMinMaxDate();
    this.setPickerType();

    let outputValue: DatePickerDate | Range | CinemaWeek;

    if (this.isRangePicker) {
      if (this.range.from && !this.range.to) {
        this.range.to = cloneDeep(this.range.from);
      }

      outputValue = cloneDeep(this.range);

      if (!this.range.from && !this.range.to) {
        this.minDate ? this.currentDateToShow = dayjs(this.minDate) : this.backToToday();
      } else {
        this.currentDateToShow = dayjs(this.range.from);
        this.generateCalendar();
      }

      if (this.outputType === 'string') {
        outputValue = {
          from: this.range.from?.format(),
          to: this.range.to?.format(),
        };
      } else if (this.outputType === 'date') {
        outputValue = {
          from: this.range.from?.toDate(),
          to: this.range.to?.toDate(),
        };
      }
    } else if (this.isKWPicker) {
      outputValue = cloneDeep(this.selectedCinemaWeek);

      if (!this.selectedCinemaWeek.week) {
        this.minDate ? this.currentDateToShow = dayjs(this.minDate) : this.backToToday();
      } else {
        this.currentDateToShow = dayjs(this.selectedCinemaWeek.from);
      }

      if (this.outputType === 'string') {
        outputValue = {
          week: this.selectedCinemaWeek.week,
          from: this.selectedCinemaWeek.from?.format(),
          to: this.selectedCinemaWeek.to?.format(),
        };
      } else if (this.outputType === 'date') {
        outputValue = {
          week: this.selectedCinemaWeek.week,
          from: this.selectedCinemaWeek.from?.toDate(),
          to: this.selectedCinemaWeek.to?.toDate(),
        };
      }

      this.generateCalendar();
      this.onCinemaWeekBlur();
    } else {
      if (this.selectedVal && this.selectedTime && this.showTime) {
        this.selectedVal = this.selectedVal
          .set('hour', this.selectedTime.hour())
          .set('minute', this.selectedTime.minute())
          .set('second', 0);
      }

      outputValue = cloneDeep(this.selectedVal);

      if (this.selectedVal) {
        this.currentDateToShow = dayjs(this.selectedVal);
        this.generateCalendar();
      }

      if (outputValue) {
        if (this.isDayMonthView) {
          outputValue = dayjs(outputValue as dayjs.Dayjs).year(1970);
        }

        if (this.outputType === 'string') {
          outputValue = (outputValue as dayjs.Dayjs).format();
        } else if (this.outputType === 'date') {
          outputValue = (outputValue as dayjs.Dayjs).toDate();
        }
      }
    }

    this._isPickerOpened = false;
    this.destroyOverlay();
    this.propagateChanges(outputValue);
    this.cdr.detectChanges();
  }

  public override propagateChanges(data: DatePickerDate | Range | number | CinemaWeek): void {
    super.propagateChanges(data);
    this.valueChanged.emit(data);
  }

  public formatSelectedVal(value: string, pickerType: 'day' | 'range' | 'month' | 'year' | 'kw'): void {
    if (value === '') {
      this.clearDate();
    } else {
      if (pickerType === 'day') {
        this.dateChange(this.validDateInput(dayjs(this.formatDaySelectInput(value), 'DD.MM.YYYY')));
      } else if (pickerType === 'year') {
        this.yearChange(+value);
      }
    }
  }

  public onCinemaWeekMouseEnter(date: dayjs.Dayjs): void {
    this.currentHoveredCinemaWeek = CalendarUtils.getCinemaWeek(date).week;
    this.cdr.detectChanges();
  }

  public onCinemaWeekFocus(date: dayjs.Dayjs): void {
    this.currentFocusedCinemaWeek = CalendarUtils.getCinemaWeek(date).week;
    this.cdr.detectChanges();
  }

  public onCinemaWeekMouseOut(): void {
    this.currentHoveredCinemaWeek = null;
    this.cdr.detectChanges();
  }

  public onCinemaWeekBlur(): void {
    this.currentFocusedCinemaWeek = null;
    this.cdr.detectChanges();
  }

  public onInputDblclick(input: HTMLInputElement): void {
    const [dayLength, monthLength, yearLength] = input.value.split('.').map((x: string) => x.length);
    const dayStart = 0;
    const dayEnd = dayLength;
    const monthStart = dayEnd + 1;
    const monthEnd = monthStart + monthLength;
    const yearStart = monthEnd + 1;
    const yearEnd = yearStart + yearLength;

    if (this.inputSelectionStart >= dayStart && this.inputSelectionStart <= dayEnd) {
      input.setSelectionRange(dayStart, dayEnd);
    } else if (this.inputSelectionStart >= monthStart && this.inputSelectionStart <= monthEnd) {
      input.setSelectionRange(monthStart, monthEnd);
    } else if (this.inputSelectionStart >= yearStart && this.inputSelectionStart <= yearEnd) {
      input.setSelectionRange(yearStart, yearEnd);
    }
  }

  private generateDates(): void {
    const datesToRender: IDateToRender[] = [];

    for (let day = 0; day < this.currentDateToShow.daysInMonth(); day++) {
      const date = dayjs()
        .set('month', this.currentDateToShow.month())
        .set('year', this.currentDateToShow.year()).startOf('date')
        .set('date', day + 1);

      datesToRender.push(
        CalendarUtils.getDateToRender(
          dayjs(date),
          true,
          false,
          false
        )
      );
    }

    const firstDate = cloneDeep(datesToRender[0]?.date);
    const firstDateDay = firstDate.day() === 0 ? 7 : firstDate.day();

    let offset = 0;

    if (this.isKWPicker) {
      offset = firstDate.day() > 0 && firstDate.day() < 4 ? 4 : -3;
    }

    for (let day = 1; day < firstDateDay + offset; day++) {
      datesToRender.unshift(
        CalendarUtils.getDateToRender(
          dayjs(firstDate).subtract(day, 'day'),
          false,
          true,
          false
        )
      );
    }

    const lastDate = cloneDeep(datesToRender[datesToRender.length - 1].date);
    const missingDatesCount = 42 - datesToRender.length;

    for (let day = 0; day < missingDatesCount; day++) {
      datesToRender.push(
        CalendarUtils.getDateToRender(
          dayjs(lastDate).add(day + 1, 'day'),
          false,
          false,
          true
        )
      );
    }

    this.datesToRender = [...datesToRender];

    if (this.isKWPicker) {
      this.cinemaWeeks = [];
      this.datesToRender.forEach((x, index) => {
        if (index % 7 === 0) {
          this.cinemaWeeks.push(CalendarUtils.getCinemaWeek(x.date));
        }
      });

      this.cinemaWeeks = [...this.cinemaWeeks];
    }

    this.cdr.detectChanges();
  }

  private generateYears(): void {
    const currentYear = this.currentDateToShow.year();
    this.years = [currentYear];

    for (let i = 1; i < 8; i++) {
      this.years.unshift(currentYear - i);
    }

    for (let i = 1; i < 5; i++) {
      this.years.push(currentYear + i);
    }
  }

  private generateCalendar(): void {
    this.generateDates();
    this.generateYears();
    this.cdr.detectChanges();
  }

  private set currentChoiceMode(currentChoiceMode: ChoiceModes) {
    this._currentChoiceMode = currentChoiceMode;
    this.cdr.detectChanges();
  }

  private validateMinMaxDate(): void {
    if (this.selectedVal) {
      if (this.selectedVal.format('x') > this.maxDate?.format('x')) {
        this.selectedVal = dayjs(this.maxDate);
      } else if (this.selectedVal.format('x') < this.minDate?.format('x')) {
        this.selectedVal = dayjs(this.minDate);
      }

      this.currentDateToShow = dayjs(this.selectedVal);
    }
  }

  private validDateInput(val: dayjs.Dayjs): dayjs.Dayjs {
    if (val.format('x') > this.maxDate?.format('x')) {
      return this.maxDate;
    }

    if (val.format('x') < this.minDate?.format('x')) {
      return this.minDate;
    }

    return val;
  }

  private formatDaySelectInput(val: string): string {
    const day = val.slice(0, 2);
    const month = val.slice(2, 4);
    const year = val.slice(4, 8);

    return `${day}.${month}.${year}`;
  }

  private setInputValue(): void {
    if (this.pickerType === 'year') {
      this.inputValue = this.selectedVal?.format('YYYY');
    }

    if (this.pickerType === 'day') {
      if (this.showTime) {
        this.inputValue = this.selectedVal?.format('DD.MM.YYYY; HH:mm');
      } else if (this.isDayMonthView) {
        this.inputValue = this.selectedVal?.format('DD.MM');
      } else {
        this.inputValue = this.selectedVal?.format('DD.MM.YYYY');
      }
    }
  }

  private setPickerType(): void {
    switch (this.pickerType) {
      case 'range': this.currentChoiceMode = ChoiceModes.RANGE;
        break;

      case 'day': this.currentChoiceMode = ChoiceModes.DAYS;
        break;

      case 'month': this.currentChoiceMode = ChoiceModes.MONTHS;
        break;

      case 'year': this.currentChoiceMode = ChoiceModes.YEARS;
        break;

      case 'kw': this.currentChoiceMode = ChoiceModes.KW;
    }
  }

  private reset(): void {
    this.range = {
      from: null,
      to: null
    };
    this.selectedCinemaWeek = {
      week: null,
      from: null,
      to: null
    };
    this.inputValue = null;
    this.currentDateToShow = dayjs();
    this.selectedTime = null;
  }

  private onClickAnywhere(e: Event): void {
    if (
      this.isPickerOpened &&
      !this.datePickerWrapper.nativeElement.contains(e.target as HTMLElement) &&
      !(e.target as HTMLElement).closest('.picker-layer')
    ) {
      this.close(false);
    }
  }

  private setOverlay(): void {
    this.destroyOverlay();
    const positionStrategy = this._overlay.position()
      .flexibleConnectedTo(this.datePickerWrapper)
      .withPositions([{
        originX: 'start',
        originY: 'bottom',
        overlayX: 'start',
        overlayY: 'top'
      }, {
        originX: 'start',
        originY: 'top',
        overlayX: 'start',
        overlayY: 'bottom'
      }]);

    const scrollStrategy = this._scrollStrategyOptions.reposition();

    this._overlayRef = this._overlay.create({
      positionStrategy,
      scrollStrategy
    });

    this._overlayRef.attach(new TemplatePortal(this.pickerLayer, this._viewContainerRef));
  }

  private setupIntersectionObserver(): void {
    this._intersectionObserver = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting && this.isPickerOpened) {
            this.close(false);
          }
        });
      },
      { threshold: 0 }
    );

    this._intersectionObserver.observe(this.datePickerWrapper.nativeElement);
  }

  private loadShows(): void {
    if (this.isForShows) {
      this._showsService.getProgram(
        this.currentDateToShow.startOf('month').subtract(14, 'days'),
        this.currentDateToShow.endOf('month').add(14, 'days')
      )
        .subscribe({
          next: () => this.generateCalendar(),
          error: ({ error }: ApiErrorResponse) => {
            this.notificationService.pushError(
              'Fehler'.trans('notifications/error.Title'),
              error.message.trans(`get-program/notifications/${error.message}.Message`)
            );
          }
        });
    } else {
      this.generateCalendar();
    }
  }

  private destroyOverlay(): void {
    this._overlayRef?.detach();
    this._overlayRef?.dispose();
  }

  public ngOnDestroy(): void {
    if (this._intersectionObserver) {
      this._intersectionObserver.disconnect();
    }
  }
}
